import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Spin, Space, Divider } from 'antd';
import { UserOutlined, LockOutlined, PhoneOutlined, MailOutlined, UserAddOutlined, DashboardOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
const { Title } = Typography;

const AdminRegister = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();
    
    // Mobile responsive state
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const onFinish = async (values) => {
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            // Create admin user
            const response = await api.post('/api/auth/register', {
                name: values.name,
                email: values.email,
                password: values.password,
                phone: values.phone,
            });

            if (response.status === 201) {
                // Update user role to admin
                const token = response.data.token;
                // Set token temporarily for this request
                localStorage.setItem('adminToken', token);
                await api.updateUser(response.data.user.id, { role: 'admin' });

                setSuccess('Admin user created successfully! You can now login.');
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '20px' : '40px',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Background Decorations */}
            <div style={{
                position: 'absolute',
                top: '-30%',
                right: '-15%',
                width: '500px',
                height: '500px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '50%',
                filter: 'blur(80px)'
            }} />
            <div style={{
                position: 'absolute',
                bottom: '-20%',
                left: '-10%',
                width: '350px',
                height: '350px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '50%',
                filter: 'blur(60px)'
            }} />

            <Card
                style={{
                    width: isMobile ? '100%' : '480px',
                    maxWidth: '480px',
                    borderRadius: isMobile ? '24px' : '32px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    border: 'none',
                    backdropFilter: 'blur(20px)',
                    background: 'rgba(255,255,255,0.95)',
                    position: 'relative',
                    zIndex: 1,
                    maxHeight: '90vh',
                    overflowY: 'auto'
                }}
                bodyStyle={{
                    padding: isMobile ? '32px 24px' : '40px 32px'
                }}
            >
                {/* Enhanced Header */}
                <div style={{ textAlign: 'center', marginBottom: isMobile ? '28px' : '32px' }}>
                    {/* Logo Container */}
                    <div style={{
                        background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                        borderRadius: '20px',
                        width: isMobile ? '72px' : '80px',
                        height: isMobile ? '72px' : '80px',
                        margin: '0 auto 20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 32px rgb(255, 255, 255)',
                        padding: '10px'
                    }}>
                        <img 
                            src={require('../styles/logo.png')} 
                            alt="Trading Admin Logo"
                            style={{ 
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                borderRadius: '8px'
                            }} 
                        />
                    </div>

                    <Title level={isMobile ? 4 : 3} style={{ 
                        margin: '0 0 6px 0',
                        background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontSize: isMobile ? '20px' : '24px',
                        fontWeight: 800,
                        letterSpacing: '-0.5px'
                    }}>
                        Create Admin Account
                    </Title>
                    <p style={{ 
                        color: '#666', 
                        fontSize: isMobile ? '13px' : '14px',
                        fontWeight: 500,
                        margin: 0
                    }}>
                        👤 Register a new admin user
                    </p>
                </div>

                {error && (
                    <Alert
                        message="❌ Registration Failed"
                        description={error}
                        type="error"
                        showIcon={false}
                        style={{ 
                            marginBottom: '20px',
                            borderRadius: '12px',
                            border: '1px solid #ffccc7',
                            background: 'linear-gradient(135deg, #fff2f0 0%, #ffebe8 100%)'
                        }}
                    />
                )}

                {success && (
                    <Alert
                        message="✅ Registration Successful"
                        description={success}
                        type="success"
                        showIcon={false}
                        style={{ 
                            marginBottom: '20px',
                            borderRadius: '12px',
                            border: '1px solid #b7eb8f',
                            background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)'
                        }}
                    />
                )}

                <Form
                    name="adminRegister"
                    onFinish={onFinish}
                    autoComplete="off"
                    size="large"
                    layout="vertical"
                >
                    <Form.Item
                        label={
                            <span style={{ 
                                fontSize: isMobile ? '13px' : '14px', 
                                fontWeight: 600,
                                color: '#333'
                            }}>
                                👤 Full Name
                            </span>
                        }
                        name="name"
                        rules={[
                            { required: true, message: 'Please input your name!' },
                            { min: 2, message: 'Name must be at least 2 characters!' }
                        ]}
                    >
                        <Input
                            prefix={<UserOutlined style={{ color: '#52c41a' }} />}
                            placeholder="Enter your full name"
                            style={{
                                height: isMobile ? '44px' : '48px',
                                borderRadius: '10px',
                                fontSize: isMobile ? '15px' : '14px',
                                border: '2px solid #f0f0f0',
                                transition: 'all 0.3s ease'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#52c41a';
                                e.target.style.boxShadow = '0 0 0 3px rgba(82, 196, 26, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#f0f0f0';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{ 
                                fontSize: isMobile ? '13px' : '14px', 
                                fontWeight: 600,
                                color: '#333'
                            }}>
                                📧 Email Address
                            </span>
                        }
                        name="email"
                        rules={[
                            { required: true, message: 'Please input your email!' },
                            { type: 'email', message: 'Please enter a valid email!' }
                        ]}
                    >
                        <Input
                            prefix={<MailOutlined style={{ color: '#52c41a' }} />}
                            placeholder="Enter your email address"
                            style={{
                                height: isMobile ? '44px' : '48px',
                                borderRadius: '10px',
                                fontSize: isMobile ? '15px' : '14px',
                                border: '2px solid #f0f0f0',
                                transition: 'all 0.3s ease'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#52c41a';
                                e.target.style.boxShadow = '0 0 0 3px rgba(82, 196, 26, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#f0f0f0';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{ 
                                fontSize: isMobile ? '13px' : '14px', 
                                fontWeight: 600,
                                color: '#333'
                            }}>
                                📱 Phone Number
                            </span>
                        }
                        name="phone"
                        rules={[
                            { required: true, message: 'Please input your phone number!' },
                            { pattern: /^[+]?[0-9]{10,15}$/, message: 'Please enter a valid phone number!' }
                        ]}
                    >
                        <Input
                            prefix={<PhoneOutlined style={{ color: '#52c41a' }} />}
                            placeholder="Enter your phone number"
                            style={{
                                height: isMobile ? '44px' : '48px',
                                borderRadius: '10px',
                                fontSize: isMobile ? '15px' : '14px',
                                border: '2px solid #f0f0f0',
                                transition: 'all 0.3s ease'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#52c41a';
                                e.target.style.boxShadow = '0 0 0 3px rgba(82, 196, 26, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#f0f0f0';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{ 
                                fontSize: isMobile ? '13px' : '14px', 
                                fontWeight: 600,
                                color: '#333'
                            }}>
                                🔒 Password
                            </span>
                        }
                        name="password"
                        rules={[
                            { required: true, message: 'Please input your password!' },
                            { min: 6, message: 'Password must be at least 6 characters!' }
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined style={{ color: '#52c41a' }} />}
                            placeholder="Enter your password"
                            style={{
                                height: isMobile ? '44px' : '48px',
                                borderRadius: '10px',
                                fontSize: isMobile ? '15px' : '14px',
                                border: '2px solid #f0f0f0',
                                transition: 'all 0.3s ease'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#52c41a';
                                e.target.style.boxShadow = '0 0 0 3px rgba(82, 196, 26, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#f0f0f0';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{ 
                                fontSize: isMobile ? '13px' : '14px', 
                                fontWeight: 600,
                                color: '#333'
                            }}>
                                🔐 Confirm Password
                            </span>
                        }
                        name="confirmPassword"
                        dependencies={['password']}
                        rules={[
                            { required: true, message: 'Please confirm your password!' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Passwords do not match!'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined style={{ color: '#52c41a' }} />}
                            placeholder="Confirm your password"
                            style={{
                                height: isMobile ? '44px' : '48px',
                                borderRadius: '10px',
                                fontSize: isMobile ? '15px' : '14px',
                                border: '2px solid #f0f0f0',
                                transition: 'all 0.3s ease'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#52c41a';
                                e.target.style.boxShadow = '0 0 0 3px rgba(82, 196, 26, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#f0f0f0';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: '20px' }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            icon={!loading && <UserAddOutlined />}
                            style={{
                                width: '100%',
                                height: isMobile ? '48px' : '52px',
                                borderRadius: '12px',
                                fontSize: isMobile ? '15px' : '16px',
                                fontWeight: 600,
                                background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                                border: 'none',
                                boxShadow: '0 8px 24px rgba(82, 196, 26, 0.4)',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                if (!loading) {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 12px 32px rgba(82, 196, 26, 0.5)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!loading) {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 8px 24px rgba(82, 196, 26, 0.4)';
                                }
                            }}
                        >
                            {loading ? (
                                <Space>
                                    <Spin size="small" />
                                    <span>Creating Account...</span>
                                </Space>
                            ) : (
                                '🚀 Create Admin Account'
                            )}
                        </Button>
                    </Form.Item>

                    <Divider style={{ 
                        margin: '20px 0',
                        borderColor: '#f0f0f0'
                    }}>
                        <span style={{ 
                            color: '#999', 
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: 500
                        }}>
                            Already have an account?
                        </span>
                    </Divider>

                    <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
                        <Button 
                            type="link" 
                            onClick={() => navigate('/login')}
                            style={{
                                fontSize: isMobile ? '13px' : '14px',
                                fontWeight: 600,
                                color: '#52c41a',
                                padding: isMobile ? '6px 12px' : '8px 16px',
                                borderRadius: '8px',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = 'rgba(82, 196, 26, 0.1)';
                                e.target.style.color = '#73d13d';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'transparent';
                                e.target.style.color = '#52c41a';
                            }}
                        >
                            🔐 Sign In to Dashboard
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default AdminRegister;